import type { WidgetDefinition } from "../types";
import { STANDARD_SIZES as ST } from "../types";

import {
  IconAltitude,
  IconAltitudeLine,
  IconChart,
  IconClock,
  IconCompass,
  IconDrone,
  IconList,
  IconTrend,
} from "../widgets/icons";
import {
  AltitudeBandWidget,
  AvgSmoothnessWidget,
  AvgSpeedWidget,
  BatteryDrainWidget,
  ClimbDescentWidget,
  DistanceCumulativeWidget,
  FlightDurationWidget,
  FlightListWidget,
  FlightsInWindowWidget,
  PeakGKpiWidget,
  PeakGTrendWidget,
  SmoothnessTrendWidget,
  SpeedBreakdownWidget,
  TotalDistanceKpiWidget,
  VerticalRateWidget,
} from "./PerformanceWidgets";

export const PERFORMANCE_WIDGETS: WidgetDefinition[] = [
  // KPIs (XS only — small tiles).
  {
    type: "perf_flights_in_window",
    title: "In window",
    description: "Flights matching the current mission + date filters.",
    category: "overview",
    sizes: { xs: ST.xs },
    defaultSize: "xs",
    Icon: IconDrone,
    Component: FlightsInWindowWidget,
  },
  {
    type: "perf_avg_smoothness",
    title: "Avg smoothness",
    description: "Mean smoothness score across window flights.",
    category: "performance",
    sizes: { xs: ST.xs },
    defaultSize: "xs",
    Icon: IconTrend,
    Component: AvgSmoothnessWidget,
  },
  {
    type: "perf_avg_speed",
    title: "Avg speed",
    description: "Mean avg speed across window flights (km/h).",
    category: "performance",
    sizes: { xs: ST.xs },
    defaultSize: "xs",
    Icon: IconChart,
    Component: AvgSpeedWidget,
  },
  {
    type: "perf_peak_g_kpi",
    title: "Peak G",
    description: "Single highest peak G recorded in the window.",
    category: "performance",
    sizes: { xs: ST.xs },
    defaultSize: "xs",
    Icon: IconTrend,
    Component: PeakGKpiWidget,
  },
  {
    type: "perf_total_distance_kpi",
    title: "Total distance",
    description: "Sum of distances across all window flights.",
    category: "performance",
    sizes: { xs: ST.xs },
    defaultSize: "xs",
    Icon: IconCompass,
    Component: TotalDistanceKpiWidget,
  },
  // Charts (S/M/L).
  {
    type: "perf_smoothness_trend",
    title: "Smoothness trend",
    description: "Smoothness score per flight in the window.",
    category: "performance",
    kind: "chart",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconTrend,
    Component: SmoothnessTrendWidget,
  },
  {
    type: "perf_speed_breakdown",
    title: "Speed breakdown",
    description: "Min / avg / max speed per flight.",
    category: "performance",
    kind: "chart",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconChart,
    Component: SpeedBreakdownWidget,
  },
  {
    type: "perf_altitude_band",
    title: "Altitude band",
    description: "Min / avg / max altitude per flight.",
    category: "performance",
    kind: "chart",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconAltitude,
    Component: AltitudeBandWidget,
  },
  {
    type: "perf_peak_g_trend",
    title: "Peak G trend",
    description: "Peak G recorded per flight.",
    category: "performance",
    kind: "chart",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconTrend,
    Component: PeakGTrendWidget,
  },
  {
    type: "perf_battery_drain",
    title: "Battery drain",
    description: "Battery % spent per flight.",
    category: "performance",
    kind: "chart",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconChart,
    Component: BatteryDrainWidget,
  },
  {
    type: "perf_flight_duration",
    title: "Flight duration",
    description: "Flight duration (minutes) per flight.",
    category: "performance",
    kind: "chart",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconClock,
    Component: FlightDurationWidget,
  },
  {
    type: "perf_distance_cumulative",
    title: "Cumulative distance",
    description: "Running total of distance across the window.",
    category: "performance",
    kind: "chart",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconCompass,
    Component: DistanceCumulativeWidget,
  },
  {
    type: "perf_climb_descent",
    title: "Climb / descent",
    description: "Total metres climbed vs. descended per flight.",
    category: "performance",
    kind: "chart",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconAltitudeLine,
    Component: ClimbDescentWidget,
  },
  {
    type: "perf_vertical_rate",
    title: "Vertical rate",
    description: "Max climb and descent rate per flight.",
    category: "performance",
    kind: "chart",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "m",
    Icon: IconAltitudeLine,
    Component: VerticalRateWidget,
  },
  {
    type: "perf_flight_list",
    title: "Flight list",
    description: "Recent flights in the current window as a compact table.",
    category: "overview",
    sizes: { s: ST.s, m: ST.m, l: ST.l },
    defaultSize: "l",
    Icon: IconList,
    Component: FlightListWidget,
  },
];

// Default layout: 5 XS KPIs on row 1, then 4 M charts (smoothness / speed /
// altitude / peak G), then flight list + 2 more M charts.
export const DEFAULT_PERFORMANCE_LAYOUT: string[] = [
  "perf_flights_in_window",
  "perf_avg_smoothness",
  "perf_avg_speed",
  "perf_peak_g_kpi",
  "perf_smoothness_trend",
  "perf_speed_breakdown",
  "perf_altitude_band",
  "perf_peak_g_trend",
  "perf_battery_drain",
  "perf_climb_descent",
  "perf_flight_duration",
  "perf_flight_list",
];
